/*
 * frontend/my-react-app/src/App.js
 * Created: 2025-10-18
 * Updated: 2026-03-09 — added /medications route (SignedIn + PetProvider); imported MedicationsPage
 * Updated: 2026-03-10 — added /medications/new route + AddMedicationRoute; /medications/:id/edit + EditMedicationRoute
 * Updated: 2026-03-10 — URL cleanup: /d dashboard, /meds canonical, /medications redirects
 * Updated: 2026-03-10 — normalize Dashboard padding to 24px 20px (matches MedicationsPage)
 * Root router and auth guard — maps URL paths to page components.
 *
 * - / → LandingPage (signed out) or redirect to /d (signed in)
 * - /d dashboard, /dashboard placeholder, /meds, /meds/new, /meds/:id/edit
 */

import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, Link } from 'react-router-dom';
import { SignedIn, SignedOut, useAuth } from '@clerk/clerk-react';
import { PetProvider, usePet } from './contexts/PetContext';
import Header from './Header';
import DayNavigation from './DayNavigation';
import TimeSlotContainer from './TimeSlotContainer';
import SignInPage from './pages/SignInPage';
import SignUpPage from './pages/SignUpPage';
import LandingPage from './pages/LandingPage';
import MedicationsPage from './pages/MedicationsPage';
import MedicationForm from './pages/MedicationForm';
import { fetchMedications, fetchMedication, createMedication, updateMedication, stopMedication } from './services/api';
import './App.css';

/*
 * AddMedicationRoute — thin wrapper that supplies MedicationForm with pet context,
 * user timezone (from GET /api/medications), and the onSave handler.
 * Lives inside PetProvider so usePet() is available.
 */
function AddMedicationRoute() {
  const { selectedPet, isLoading: petsLoading } = usePet();
  const { getToken } = useAuth();

  const [userTimezone, setUserTimezone] = useState(null);

  useEffect(() => {
    if (!selectedPet) return;
    fetchMedications(getToken, selectedPet.id)
      .then(({ userTimezone: tz }) => setUserTimezone(tz))
      .catch(() => {});
  }, [selectedPet, getToken]);

  if (petsLoading) {
    return (
      <div style={{ padding: '48px', textAlign: 'center', color: '#6b7280' }}>
        Loading…
      </div>
    );
  }

  if (!selectedPet) {
    return <Navigate to="/meds" replace />;
  }

  async function handleSave(formData) {
    await createMedication(getToken, selectedPet.id, formData);
  }

  return (
    <MedicationForm
      petId={selectedPet.id}
      petName={selectedPet.name}
      userTimezone={userTimezone}
      onSave={handleSave}
    />
  );
}

/* Redirect /medications/:id/edit → /meds/:id/edit */
function MedEditRedirect() {
  const { id } = useParams();
  return <Navigate to={`/meds/${id}/edit`} replace />;
}

/*
 * EditMedicationRoute — fetches medication by ID, supplies MedicationForm with
 * initialValues and onSave/onStop handlers for edit mode.
 */
function EditMedicationRoute() {
  const { id } = useParams();
  const { selectedPet, isLoading: petsLoading } = usePet();
  const { getToken } = useAuth();

  const [med, setMed] = useState(null);
  const [userTimezone, setUserTimezone] = useState(null);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    if (!id) return;
    fetchMedication(getToken, id)
      .then(({ medication, userTimezone: tz }) => {
        setMed(medication);
        setUserTimezone(tz);
      })
      .catch(() => setLoadError('Medication not found.'));
  }, [id, getToken]);

  if (petsLoading || (!med && !loadError)) {
    return (
      <div style={{ padding: '48px', textAlign: 'center', color: '#6b7280' }}>
        Loading…
      </div>
    );
  }

  if (loadError) {
    return (
      <div style={{ padding: '48px', textAlign: 'center', color: '#6b7280' }}>
        <p>{loadError}</p>
        <Link to="/meds" style={{ color: '#059669', marginTop: '16px', display: 'block' }}>
          ← Back to medications
        </Link>
      </div>
    );
  }

  if (!selectedPet) {
    return <Navigate to="/meds" replace />;
  }

  return (
    <MedicationForm
      petId={selectedPet.id}
      petName={selectedPet.name}
      userTimezone={userTimezone}
      initialValues={med}
      onSave={(formData) => updateMedication(getToken, id, formData)}
      onStop={() => stopMedication(getToken, id)}
    />
  );
}

/*
 * Dashboard Component - Your medication tracking interface
 * Now wrapped with PetProvider so all components can access selected pet
 */
function Dashboard() {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const earliestDate = new Date('2025-09-01');

  const handleDateChange = (newDate) => {
    setSelectedDate(newDate);
  };

   // ADD THIS FUNCTION TO HANDLE RESET TO TODAY
  const handleResetToToday = () => {
    setSelectedDate(new Date());
  }; 
  
  return (
    <div className="App">
      <Header onTodayClick={handleResetToToday} />
      
      <div style={{ 
        padding: '24px 20px', 
        backgroundColor: '#f9fafb', 
        minHeight: 'calc(100vh - 64px)'
      }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <DayNavigation 
            selectedDate={selectedDate}
            onDateChange={handleDateChange}
            earliestDate={earliestDate}
          />
          
          <TimeSlotContainer selectedDate={selectedDate} />
        </div>
      </div>
    </div>
  );
}

/*
 * Main App Component - Handles routing and authentication
 */
function App() {
  return (
    <Router>
      <Routes>
        {/* Public routes - anyone can access these */}
        <Route path="/sign-in/*" element={<SignInPage />} />
        <Route path="/sign-up/*" element={<SignUpPage />} />
        
        {/* Root: landing page (signed out) or redirect to /d (signed in) */}
        <Route
          path="/"
          element={
            <>
              <SignedIn>
                <Navigate to="/d" replace />
              </SignedIn>
              <SignedOut>
                <LandingPage />
              </SignedOut>
            </>
          }
        />

        {/* Dashboard — single pet schedule */}
        <Route
          path="/d"
          element={
            <>
              <SignedIn>
                <PetProvider>
                  <Dashboard />
                </PetProvider>
              </SignedIn>
              <SignedOut>
                <Navigate to="/sign-in" replace />
              </SignedOut>
            </>
          }
        />

        {/* Future aggregate dashboard — placeholder */}
        <Route
          path="/dashboard"
          element={<Navigate to="/d" replace />}
        />

        {/* Medications redirects (backwards compatibility) */}
        <Route path="/medications" element={<Navigate to="/meds" replace />} />
        <Route path="/medications/new" element={<Navigate to="/meds/new" replace />} />
        <Route path="/medications/:id/edit" element={<MedEditRedirect />} />

        {/* Medications management — canonical routes */}
        <Route
          path="/meds"
          element={
            <>
              <SignedIn>
                <PetProvider>
                  <MedicationsPage />
                </PetProvider>
              </SignedIn>
              <SignedOut>
                <Navigate to="/sign-in" replace />
              </SignedOut>
            </>
          }
        />
        <Route
          path="/meds/new"
          element={
            <>
              <SignedIn>
                <PetProvider>
                  <AddMedicationRoute />
                </PetProvider>
              </SignedIn>
              <SignedOut>
                <Navigate to="/sign-in" replace />
              </SignedOut>
            </>
          }
        />
        <Route
          path="/meds/:id/edit"
          element={
            <>
              <SignedIn>
                <PetProvider>
                  <EditMedicationRoute />
                </PetProvider>
              </SignedIn>
              <SignedOut>
                <Navigate to="/sign-in" replace />
              </SignedOut>
            </>
          }
        />
      </Routes>
    </Router>
  );
}

export default App;